import { resolveClient } from './supabase';
import type { SupabaseClient } from '@supabase/supabase-js';

export interface RawData {
  id: string;
  tenant_id: string;
  connection_id: string | null;
  source_system: string;
  object_type: string;
  data: Record<string, unknown>;
  synced_at: string;
  processed_at: string | null;
  failed_at: string | null;
  error_message: string | null;
}

/**
 * Get a raw_data record by ID
 */
export async function getRawData(id: string, client?: SupabaseClient): Promise<RawData | null> {
  const supabase = resolveClient(client);

  const { data, error } = await supabase.from('raw_data').select('*').eq('id', id).single();

  if (error) {
    if (error.code === 'PGRST116') return null; // Not found
    throw new Error(error.message);
  }

  return data as RawData;
}

/**
 * Get all raw_data records linked to a node via source_raw_data_id
 * Searches the node itself and all PMN nodes connected to it
 * RLS handles tenant filtering automatically
 */
export async function getRawDataForNode(nodeId: string, client?: SupabaseClient): Promise<RawData[]> {
  const supabase = resolveClient(client);

  // Get the main node's source_raw_data_id(s)
  const { data: node } = await supabase.from('nodes').select('props').eq('id', nodeId).single();

  const rawDataIds = new Set<string>();

  // Check main node - support both array and single value for backward compatibility
  if (node?.props?.source_raw_data_ids && Array.isArray(node.props.source_raw_data_ids)) {
    // New format: array of source IDs
    for (const id of node.props.source_raw_data_ids) {
      if (typeof id === 'string') rawDataIds.add(id);
    }
  } else if (node?.props?.source_raw_data_id) {
    // Old format: single source ID (backward compatibility)
    rawDataIds.add(node.props.source_raw_data_id as string);
  }

  // Get PMN nodes connected via HAS_PROPERTY, HAS_METRIC, HAS_NARRATIVE
  const { data: pmnEdges } = await supabase.from('edges').select('dst').eq('src', nodeId).in('type', ['HAS_PROPERTY', 'HAS_METRIC', 'HAS_NARRATIVE']).is('deleted_at', null);

  if (pmnEdges && pmnEdges.length > 0) {
    const pmnNodeIds = pmnEdges.map((e) => e.dst);

    const { data: pmnNodes } = await supabase.from('nodes').select('props').in('id', pmnNodeIds);

    if (pmnNodes) {
      for (const pmn of pmnNodes) {
        if (pmn.props?.source_raw_data_id) {
          rawDataIds.add(pmn.props.source_raw_data_id as string);
        }
      }
    }
  }

  if (rawDataIds.size === 0) {
    return [];
  }

  // Fetch all raw_data records - RLS handles tenant filtering
  const { data: rawDataRecords, error } = await supabase.from('raw_data').select('*').in('id', Array.from(rawDataIds));

  if (error) throw new Error(error.message);

  return (rawDataRecords as RawData[]) || [];
}

/**
 * Search raw_data for potential matches to a node by name within the same org domain
 * Useful for finding enrichment data that might apply to existing nodes
 */
export async function findRawDataMatchesForNode(_nodeId: string, nodeType: string, nodeName: string, client?: SupabaseClient): Promise<RawData[]> {
  const supabase = resolveClient(client);

  // Get raw_data records that might contain this person/org - RLS handles tenant filtering
  const { data, error } = await supabase.from('raw_data').select('*').eq('source_system', 'hunter').eq('object_type', 'enrichment');

  if (error) throw new Error(error.message);
  if (!data) return [];

  // Filter for records that contain matching names
  const matches: RawData[] = [];
  const searchName = nodeName.toLowerCase();

  for (const record of data as RawData[]) {
    if (nodeType === 'Person') {
      // Search in domain_search_response.emails
      const emails = (record.data?.domain_search_response as Record<string, unknown>)?.emails as Array<Record<string, unknown>> | undefined;
      if (emails) {
        for (const email of emails) {
          const firstName = ((email.first_name as string) || '').toLowerCase();
          const lastName = ((email.last_name as string) || '').toLowerCase();
          const fullName = `${firstName} ${lastName}`.trim();
          if (fullName && searchName.includes(firstName) && searchName.includes(lastName)) {
            matches.push(record);
            break;
          }
        }
      }
    } else if (nodeType === 'Organization') {
      // Search in company_response.name
      const companyName = (((record.data?.company_response as Record<string, unknown>)?.name as string) || '').toLowerCase();
      if (companyName && searchName.includes(companyName)) {
        matches.push(record);
      }
    }
  }

  return matches;
}
